'use client';

import configuration from '@/lib/config/site';
import Link from 'next/link';
import { SocialIcons } from './Icons';
import routes from '@/lib/config/routes';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { toast } from 'react-toastify';
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/Form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { NewsletterSubscribersSchema, NewsletterSubscribers } from '@/lib/types/validations';
import { useRouter } from 'next/navigation';
import { CreateNewsletterSubscribers } from '@/lib/API/Database/newsletterSubscribers/mutations';
import { SendSubscribersEmail } from '@/lib/API/Services/newsletterSubscribers/newsletterSubscribers';
import { Icons } from '@/components/Icons';
import { ArrowRight, Mail } from 'lucide-react';
import { MainLogoText } from './MainLogo';
import CookieSettingsButton from './CookieSettingsButton';

export default function Footer() {
  const { footer_nav } = routes;
  const router = useRouter();

  const form = useForm<NewsletterSubscribers>({
    resolver: zodResolver(NewsletterSubscribersSchema),
    defaultValues: {
      email: '',
    },
  });

  const { 
    reset,
    register,
    formState: { isSubmitting }
  } = form;

  const onSubmit = async (values: NewsletterSubscribers) => {
    const { email } = values;

    try {
      await CreateNewsletterSubscribers({ email });
      await SendSubscribersEmail({ email });

      reset(); 
      toast.success('You have successfully subscribed to our newsletter');
      router.refresh(); 
    } catch (err) {
      toast.error('Failed to subscribe. Please try again.');
      throw err;
    }
  };

  return (
    <footer className="relative bg-slate-950 border-t border-white/5">
      {/* Subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-amber-950/10 to-transparent pointer-events-none" />
      
      <div className="relative container mx-auto px-6 py-16 lg:py-20">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-4">
            <div className="mb-6">
              <MainLogoText />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-sm">
              AI-powered group matching platform that helps organizations create teams, assign roommates, and build connections that last.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <Link 
                href={configuration.links.linkedin}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 hover:bg-amber-700/20 border border-white/10 flex items-center justify-center transition-all duration-300 group"
              >
                <SocialIcons.Linkedin className="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
              </Link>
              <Link 
                href="https://x.com/EasyPairin59049"
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 hover:bg-amber-700/20 border border-white/10 flex items-center justify-center transition-all duration-300 group"
              >
                <SocialIcons.Twitter className="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
              </Link>
              <Link 
                href="mailto:hello@easypairing.com"
                className="w-10 h-10 rounded-lg bg-white/5 hover:bg-amber-700/20 border border-white/10 flex items-center justify-center transition-all duration-300 group"
              >
                <Mail className="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
              </Link>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="lg:col-span-5 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-semibold text-sm mb-4">
                {footer_nav.about.title}
              </h3>
              <ul className="space-y-3">
                {footer_nav.about.routes.map((item) => (
                  <li key={item.title}>
                    <Link
                      href={item.link}
                      className="text-sm text-slate-400 hover:text-amber-500 transition-colors"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm mb-4">
                {footer_nav.useCases.title}
              </h3>
              <ul className="space-y-3">
                {footer_nav.useCases.routes.map((item) => (
                  <li key={item.title}>
                    <Link
                      href={item.link}
                      className="text-sm text-slate-400 hover:text-amber-500 transition-colors"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm mb-4">
                {footer_nav.resources.title}
              </h3>
              <ul className="space-y-3">
                {footer_nav.resources.routes.map((item) => (
                  <li key={item.title}>
                    <Link
                      href={item.link}
                      className="text-sm text-slate-400 hover:text-amber-500 transition-colors"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm mb-4">
                {footer_nav.legal.title}
              </h3>
              <ul className="space-y-3">
                {footer_nav.legal.routes.map((item) => (
                  <li key={item.title}>
                    <Link
                      href={item.link}
                      className="text-sm text-slate-400 hover:text-amber-500 transition-colors"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="lg:col-span-3">
            <h3 className="text-white font-semibold text-sm mb-2">
              Stay Updated
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Get the latest news, tips, and product updates.
            </p>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-3"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormMessage className="text-xs" />
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...register('email')}
                            type="email"
                            placeholder="Enter your email"
                            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 pr-12 focus:border-amber-600/50 focus:ring-amber-600/20"
                            {...field}
                          />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button 
                  disabled={isSubmitting} 
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-medium"
                >
                  {isSubmitting ? (
                    <Icons.Spinner className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="mr-2 h-4 w-4" />
                  )}
                  Subscribe
                </Button>
              </form>
            </Form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} Easy Pairing, Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link
              href="/privacypolicy"
              className="text-slate-500 hover:text-amber-500 transition-colors"
            >
              Privacy
            </Link>
            <Link
              href="/termsandconditions"
              className="text-slate-500 hover:text-amber-500 transition-colors"
            >
              Terms
            </Link>
            <Link
              href="/contact"
              className="text-slate-500 hover:text-amber-500 transition-colors"
            >
              Contact
            </Link>
            <CookieSettingsButton />
          </div>
        </div>
      </div>
    </footer>
  );
}
